import { type PageIntroCopyProps } from '@/components/molecules';
import { type PageIntroInput, usePageIntroLayout } from '@/hooks/usePageIntroLayout';

import { PageIntroFeature } from './PageIntroFeature';
import { PageIntroPlain } from './PageIntroPlain';

export type PageIntroProps = PageIntroCopyProps & PageIntroInput & { compact?: boolean };

/**
 * Opening band of an inner page. Holds the page's only h1.
 *
 * Two layouts, and this picks between them and nothing else. It used to hold
 * both trees itself, which meant one component under one name rendering two
 * unrelated things and every reader having to work out which half applied;
 * the branch is a one-line choice now and each layout is answerable on its
 * own. Every decision behind the choice — which layout, which ink, whether
 * there are enough proof points to draw a strip — is in `usePageIntroLayout`,
 * so nothing in this file decides anything it also draws.
 *
 * Existing page props remain unchanged; compact catalogue pages can opt into
 * a tighter copy arrangement without changing the other routes.
 */
export function PageIntro({
  slideshow,
  slides,
  highlights,
  keepOnPhone,
  compact,
  ...copy
}: PageIntroProps) {
  const layout = usePageIntroLayout({ slideshow, slides, highlights, keepOnPhone });

  return layout.variant === 'feature' ? (
    <PageIntroFeature
      {...copy}
      slides={layout.slides}
      highlights={layout.highlights}
      onPhone={layout.onPhone}
      compact={compact ?? false}
    />
  ) : (
    <PageIntroPlain
      {...copy}
      highlights={layout.highlights}
      onPhone={layout.onPhone}
      compact={compact ?? false}
    />
  );
}
